import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { User, Teacher, Student } from '../../data/types';
import Avatar from '../ui/Avatar';

const UserProfileScreen: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const navigate = useNavigate();

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
      setFormData({
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        email: currentUser.email,
      });
    } else {
      // Redirect to login if no user is found
      navigate('/');
    }
  }, [navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (user) {
      const updatedUser = await authService.updateUserProfile(formData);
      if (updatedUser) {
        setUser(updatedUser);
        setIsEditing(false);
      }
    }
  };

  const renderTeacherDetails = (teacher: Teacher) => (
    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-2">School Information</h3>
        <p className="text-sm text-gray-600"><span className="font-medium">School:</span> {teacher.school.name}</p>
        <p className="text-sm text-gray-600"><span className="font-medium">Grade:</span> {teacher.grade}</p>
      </div>
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Classes</h3>
        <p className="text-sm text-gray-600"><span className="font-medium">Number of Classes:</span> {teacher.classes.length}</p>
      </div>
    </div>
  );

  const renderStudentDetails = (student: Student) => (
    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-2">School Information</h3>
        <p className="text-sm text-gray-600"><span className="font-medium">School:</span> {student.school.name}</p>
        <p className="text-sm text-gray-600"><span className="font-medium">Grade:</span> {student.grade}</p>
      </div>
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Achievements</h3>
        <p className="text-sm text-gray-600">
          <span className="font-medium">Total Achievements:</span> {student.achievements.length}
        </p>
      </div>
    </div>
  );

  const renderAdminDetails = () => (
    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Administrative Information</h3>
        <p className="text-sm text-gray-600">
          <span className="font-medium">Access Level:</span> {user?.role === 'SuperAdmin' ? 'System-wide' : 'School-level'}
        </p>
      </div>
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Last Login</h3>
        <p className="text-sm text-gray-600">
          <span className="font-medium">Last Active:</span> {new Date().toLocaleDateString()}
        </p>
      </div>
    </div>
  );

  if (!user) {
    return <div className="p-4">Loading user profile...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
          >
            Edit Profile
          </button>
        ) : (
          <div className="space-x-2">
            <button
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
            >
              Save Changes
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start">
            <div className="mb-4 sm:mb-0 sm:mr-6">
              <Avatar 
                src={user.avatar} 
                name={`${user.firstName} ${user.lastName}`}
                size="xl"
              />
            </div>
            
            {isEditing ? (
              <form className="w-full" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      id="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      id="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                    />
                  </div>
                </div>
              </form>
            ) : (
              <div className="w-full">
                <h2 className="text-xl font-semibold text-gray-900">{user.firstName} {user.lastName}</h2>
                <p className="text-sm text-gray-500 mb-2">{user.role}</p>
                <p className="text-sm text-gray-600 mb-1">
                  <span className="font-medium">Username:</span> {user.username}
                </p>
                <p className="text-sm text-gray-600 mb-1">
                  <span className="font-medium">Email:</span> {user.email}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Role-specific details */}
      {user.role === 'Teacher' && renderTeacherDetails(user as Teacher)}
      {user.role === 'Student' && renderStudentDetails(user as Student)}
      {(user.role === 'SuperAdmin' || user.role === 'SchoolAdmin') && renderAdminDetails()}
    </div>
  );
};

export default UserProfileScreen;
